import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  ContentCopy as ContentCopyIcon,
  Notifications as NotificationsIcon,
  LinkOff as LinkOffIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface DeliveryCheckActionsMenuProps {
  checkId?: string;
  transferId?: string;
  outgoingRequestId?: string;
  onActionComplete?: () => void;
}

interface TokenRow {
  token: string;
  expires_at: string;
  used_at: string | null;
  revoked_at: string | null;
  driver_name?: string | null;
  driver_email?: string | null;
  driver_phone?: string | null;
  driverLink?: string;
}

interface WaiverRow {
  waiver_id: string;
  check_id: string;
  signatory_name: string;
  signatory_role?: string | null;
  signatory_company?: string | null;
  article_description: string;
  intended_use?: string | null;
  hazards_acknowledged?: any;
  waiver_text_snapshot: string;
  signature_image_path: string;
  signed_at: string;
  signed_via?: string | null;
}

const DeliveryCheckActionsMenu: React.FC<DeliveryCheckActionsMenuProps> = ({ checkId, transferId, outgoingRequestId, onActionComplete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Driver link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [externalPhone, setExternalPhone] = useState('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  // Reminder dialog
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderChannel, setReminderChannel] = useState<'push' | 'email' | 'sms'>('push');
  const [reminderRecipient, setReminderRecipient] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [reminderSuccess, setReminderSuccess] = useState<string | null>(null);

  // Waiver dialog
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [waiverLoading, setWaiverLoading] = useState(false);
  const [waiver, setWaiver] = useState<WaiverRow | null>(null);
  const [waiverError, setWaiverError] = useState<string | null>(null);

  // Cancel / archive dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  // ─── Driver link actions ───────────────────────────────────────────
  const loadTokens = async () => {
    setLinkLoading(true);
    setLinkError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/deliveries/${checkId}/external-tokens`, authHeaders());
      if (res.data?.success) {
        setTokens(res.data.tokens || []);
      } else {
        setLinkError(res.data?.error || 'Failed to load driver links');
      }
    } catch (e: any) {
      setLinkError(e?.response?.data?.error || 'Failed to load driver links');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleOpenLinkDialog = async () => {
    handleCloseMenu();
    setLinkDialogOpen(true);
    setLinkSuccess(null);
    await loadTokens();
  };

  const handleCopy = async (link: string, token: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      // ignore
    }
  };

  const handleGenerateLink = async () => {
    if (!externalName.trim() || !externalEmail.trim()) {
      setLinkError('Driver name and email are required.');
      return;
    }
    setGenerating(true);
    setLinkError(null);
    setLinkSuccess(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/deliveries/${checkId}/assign-external`,
        {
          externalDriver: {
            name: externalName.trim(),
            email: externalEmail.trim(),
            phone: externalPhone.trim() || null,
          },
        },
        authHeaders()
      );
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to generate link');
      setLinkSuccess(res.data.emailSent ? 'Magic link emailed to the driver.' : 'Magic link generated.');
      setExternalName('');
      setExternalEmail('');
      setExternalPhone('');
      await loadTokens();
      onActionComplete?.();
    } catch (e: any) {
      setLinkError(e?.response?.data?.error || e?.message || 'Failed to generate link');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeToken = async (token: string) => {
    if (!window.confirm('Revoke this driver link? They will no longer be able to complete the check via this link.')) {
      return;
    }
    try {
      const res = await axios.post(
        `${API_BASE_URL}/deliveries/${checkId}/revoke-external`,
        { token },
        authHeaders()
      );
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to revoke link');
      await loadTokens();
      onActionComplete?.();
    } catch (e: any) {
      setLinkError(e?.response?.data?.error || e?.message || 'Failed to revoke link');
    }
  };

  // ─── Reminder action ───────────────────────────────────────────────
  const handleOpenReminderDialog = () => {
    handleCloseMenu();
    setReminderDialogOpen(true);
    setReminderSuccess(null);
    setReminderError(null);
    setReminderRecipient('');
  };

  const handleSendReminder = async () => {
    if (reminderChannel !== 'push' && !reminderRecipient.trim()) {
      setReminderError(reminderChannel === 'email' ? 'Recipient email required.' : 'Recipient phone required.');
      return;
    }
    setReminderSending(true);
    setReminderError(null);
    setReminderSuccess(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/deliveries/${checkId}/send-reminder`,
        {
          channel: reminderChannel,
          recipient: reminderChannel === 'push' ? null : reminderRecipient.trim(),
        },
        authHeaders()
      );
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to send reminder');
      setReminderSuccess('Reminder dispatched.');
      onActionComplete?.();
    } catch (e: any) {
      setReminderError(e?.response?.data?.error || e?.message || 'Failed to send reminder');
    } finally {
      setReminderSending(false);
    }
  };

  // ─── Cancel / archive action ────────────────────────────────────────
  const handleOpenCancelDialog = () => {
    handleCloseMenu();
    setCancelReason('');
    setCancelError(null);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      let url: string;
      if (transferId) {
        url = `${API_BASE_URL}/deliveries/transfers/${transferId}/archive`;
      } else if (outgoingRequestId) {
        url = `${API_BASE_URL}/deliveries/outgoing-requests/${outgoingRequestId}/archive`;
      } else {
        throw new Error('No delivery ID available to cancel');
      }
      const res = await axios.post(url, { reason: cancelReason.trim() || null }, authHeaders());
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to cancel delivery');
      setCancelDialogOpen(false);
      onActionComplete?.();
    } catch (e: any) {
      setCancelError(e?.response?.data?.error || e?.message || 'Failed to cancel delivery');
    } finally {
      setCancelling(false);
    }
  };

  // ─── Waiver action ─────────────────────────────────────────────────
  const handleOpenWaiverDialog = async () => {    handleCloseMenu();
    setWaiverDialogOpen(true);
    setWaiverLoading(true);
    setWaiverError(null);
    setWaiver(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/deliveries/${checkId}/waiver`, authHeaders());
      if (res.data?.success) {
        setWaiver(res.data.waiver || null);
        if (!res.data.waiver) setWaiverError('No Section 6(8) waiver has been signed for this delivery.');
      } else {
        setWaiverError(res.data?.error || 'Failed to load waiver');
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setWaiverError('No Section 6(8) waiver has been signed for this delivery.');
      } else {
        setWaiverError(e?.response?.data?.error || 'Failed to load waiver');
      }
    } finally {
      setWaiverLoading(false);
    }
  };

  const formatHazards = (hazards: any): string[] => {
    if (!hazards) return [];
    try {
      const arr = Array.isArray(hazards) ? hazards : JSON.parse(hazards);
      return arr.map((h: any) => (typeof h === 'string' ? h : (h?.label || JSON.stringify(h))));
    } catch {
      return [];
    }
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton size="small" onClick={handleOpenMenu}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        {checkId && (
          <MenuItem onClick={handleOpenLinkDialog}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Driver links
          </MenuItem>
        )}
        {checkId && (
          <MenuItem onClick={handleOpenReminderDialog}>
            <NotificationsIcon fontSize="small" sx={{ mr: 1 }} /> Send reminder
          </MenuItem>
        )}
        {checkId && (
          <MenuItem onClick={handleOpenWaiverDialog}>
            <DescriptionIcon fontSize="small" sx={{ mr: 1 }} /> View Section 6(8) waiver
          </MenuItem>
        )}
        {checkId && (transferId || outgoingRequestId) && <Divider />}
        {(transferId || outgoingRequestId) && (
          <MenuItem onClick={handleOpenCancelDialog} sx={{ color: 'error.main' }}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} /> Cancel delivery
          </MenuItem>
        )}
      </Menu>

      {/* Driver links dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Driver magic links</DialogTitle>
        <DialogContent dividers>
          {linkError && <Alert severity="error" sx={{ mb: 2 }}>{linkError}</Alert>}
          {linkSuccess && <Alert severity="success" sx={{ mb: 2 }}>{linkSuccess}</Alert>}

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Generate new link
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField size="small" label="Driver name" value={externalName} onChange={(e) => setExternalName(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label="Driver email" type="email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label="Phone (optional)" value={externalPhone} onChange={(e) => setExternalPhone(e.target.value)} sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={handleGenerateLink}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={14} /> : <SendIcon />}
            >
              {generating ? 'Sending…' : 'Generate & email'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Existing links
          </Typography>
          {linkLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : tokens.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No magic links yet.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {tokens.map((t) => {
                const expired = !!t.expires_at && new Date(t.expires_at).getTime() < Date.now();
                const isActive = !t.revoked_at && !t.used_at && !expired;
                const link = t.driverLink || '';
                return (
                  <Box key={t.token} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t.driver_name || 'Unknown driver'} {t.driver_email ? `– ${t.driver_email}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isActive
                            ? `Active · expires ${new Date(t.expires_at).toLocaleString()}`
                            : t.used_at
                              ? `Completed ${new Date(t.used_at).toLocaleString()}`
                              : t.revoked_at
                                ? `Revoked ${new Date(t.revoked_at).toLocaleString()}`
                                : expired
                                  ? `Expired ${new Date(t.expires_at).toLocaleString()}`
                                  : ''}
                        </Typography>
                        {link && (
                          <Box
                            sx={{
                              mt: 0.5,
                              p: 0.5,
                              bgcolor: 'grey.50',
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              wordBreak: 'break-all',
                            }}
                          >
                            {link}
                          </Box>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {link && (
                          <Tooltip title={copiedToken === t.token ? 'Copied!' : 'Copy link'}>
                            <IconButton size="small" onClick={() => handleCopy(link, t.token)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isActive && (
                          <Tooltip title="Revoke link">
                            <IconButton size="small" color="error" onClick={() => handleRevokeToken(t.token)}>
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reminder dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send reminder</DialogTitle>
        <DialogContent dividers>
          {reminderError && <Alert severity="error" sx={{ mb: 2 }}>{reminderError}</Alert>}
          {reminderSuccess && <Alert severity="success" sx={{ mb: 2 }}>{reminderSuccess}</Alert>}
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Channel</Typography>
              <Stack direction="row" spacing={1}>
                {(['push', 'email', 'sms'] as const).map(ch => (
                  <Button
                    key={ch}
                    variant={reminderChannel === ch ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setReminderChannel(ch)}
                  >
                    {ch.toUpperCase()}
                  </Button>
                ))}
              </Stack>
            </Box>
            {reminderChannel !== 'push' && (
              <TextField
                fullWidth
                label={reminderChannel === 'email' ? 'Recipient email' : 'Recipient phone'}
                value={reminderRecipient}
                onChange={(e) => setReminderRecipient(e.target.value)}
                size="small"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              Push reminders are delivered to all users assigned to this check.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSendReminder}
            disabled={reminderSending}
            startIcon={reminderSending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {reminderSending ? 'Sending…' : 'Send reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waiver dialog */}
      <Dialog open={waiverDialogOpen} onClose={() => setWaiverDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Section 6(8) Waiver</DialogTitle>
        <DialogContent dividers>
          {waiverLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : waiverError && !waiver ? (
            <Alert severity="info">{waiverError}</Alert>
          ) : waiver ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signatory</Typography>
                <Typography variant="body2">{waiver.signatory_name}</Typography>
                {(waiver.signatory_role || waiver.signatory_company) && (
                  <Typography variant="caption" color="text.secondary">
                    {[waiver.signatory_role, waiver.signatory_company].filter(Boolean).join(' · ')}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Signed {new Date(waiver.signed_at).toLocaleString()} via {waiver.signed_via || 'app'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Article</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{waiver.article_description}</Typography>
              </Box>
              {waiver.intended_use && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Intended use</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{waiver.intended_use}</Typography>
                </Box>
              )}
              {formatHazards(waiver.hazards_acknowledged).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Acknowledgements</Typography>
                  <Stack component="ul" sx={{ pl: 3, m: 0 }} spacing={0}>
                    {formatHazards(waiver.hazards_acknowledged).map((h, idx) => (
                      <li key={idx}><Typography variant="body2">{h}</Typography></li>
                    ))}
                  </Stack>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Waiver text</Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 240,
                    overflowY: 'auto',
                  }}
                >
                  {waiver.waiver_text_snapshot}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                {waiver.signature_image_path ? (
                  <Box
                    component="img"
                    src={waiver.signature_image_path}
                    alt="Signature"
                    sx={{
                      mt: 0.5,
                      maxWidth: '100%',
                      maxHeight: 200,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: '#fff',
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">No signature image stored.</Typography>
                )}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiverDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Cancel delivery dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => !cancelling && setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon color="error" fontSize="small" />
          Cancel delivery
        </DialogTitle>
        <DialogContent dividers>
          {cancelError && <Alert severity="error" sx={{ mb: 2 }}>{cancelError}</Alert>}
          <DialogContentText sx={{ mb: 2 }}>
            This will archive the delivery so it no longer appears in the list. The record is retained and can be recovered by a developer if needed.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            placeholder="e.g. Created in error, template issue…"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>Keep delivery</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling…' : 'Cancel delivery'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeliveryCheckActionsMenu;
